// Formatting, date/week math, and small general-purpose helpers. Ported
// as-is from WEEKLY_2.HTM.
import { DAYS } from "./constants";

// crypto.randomUUID() (122 bits of real entropy, supported in all current
// browsers and Node 14.17+) -- not the original Math.random().toString(36)
// scheme this replaced, which could produce short, weakly-random strings
// and started producing real collisions once a single bulk import began
// generating thousands of ids in one go (recipe_ingredients.id and
// recipe_instructions.id are real primary keys, so a collision there is a
// hard failure, not just a cosmetic one). Nothing in the app parses or
// assumes a particular id format/length, so this is a drop-in swap.
export const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);

export function parseQuantity(raw) {
  if (raw == null) return 0;
  const str = String(raw).trim();
  if (!str) return 0;
  if (str.includes(" ")) {
    const [whole, frac] = str.split(" ");
    return (parseFloat(whole) || 0) + parseQuantity(frac);
  }
  if (str.includes("/")) {
    const [n, d] = str.split("/");
    const num = parseFloat(n);
    const den = parseFloat(d);
    if (!den) return 0;
    return num / den;
  }
  const n = parseFloat(str);
  return isNaN(n) ? 0 : n;
}

export function round(n, places = 2) {
  const f = Math.pow(10, places);
  return Math.round(n * f) / f;
}

// First letter upper-cased, everything else left as written ("rice vinegar" ->
// "Rice vinegar"). Used for display where a name may have been typed in any case.
export function capitalizeFirst(text) {
  const s = String(text ?? "");
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}

export function formatQuantity(num) {
  if (!num) return "0";
  const whole = Math.floor(num);
  const remainder = round(num - whole, 3);
  const fracMap = [
    [0.25, "1/4"], [0.333, "1/3"], [0.5, "1/2"],
    [0.667, "2/3"], [0.75, "3/4"],
  ];
  for (const [val, label] of fracMap) {
    if (Math.abs(remainder - val) < 0.02) {
      return whole > 0 ? `${whole} ${label}` : label;
    }
  }
  if (remainder < 0.02) return String(whole);
  return String(round(num, 2));
}

export function getWeekStartDate(date, startDay) {
  const d = new Date(date);
  const idx = DAYS.indexOf(startDay);
  const targetIdx = idx === -1 ? 0 : idx; // DAYS is Monday-first, so 0 = Monday
  const targetJsDay = (targetIdx + 1) % 7; // convert to JS getDay() convention (Sunday = 0)
  const currentJsDay = d.getDay();
  const diff = (currentJsDay - targetJsDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function orderedDays(startDay) {
  const idx = DAYS.indexOf(startDay);
  if (idx <= 0) return DAYS;
  return [...DAYS.slice(idx), ...DAYS.slice(0, idx)];
}

export function toISODate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function currentWeekStart(startDay) {
  return toISODate(getWeekStartDate(new Date(), startDay));
}

export function shiftWeek(iso, weeks) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + weeks * 7);
  return toISODate(d);
}

export function formatWeekRange(iso) {
  const start = new Date(iso + "T00:00:00");
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const startStr = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const endStr = end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `${startStr} – ${endStr}`;
}

// A duration in minutes as hours and minutes once it reaches an hour:
// 45 -> "45 min", 60 -> "1 h", 95 -> "1 h 35 min" (feedback #12).
export function formatMinutes(total) {
  const n = Math.max(0, Math.round(Number(total) || 0));
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export function formatNutrition(recipe) {
  const parts = [];
  if (recipe.caloriesPerServing > 0) parts.push(`${recipe.caloriesPerServing} cal`);
  if (recipe.proteinPerServing > 0) parts.push(`${recipe.proteinPerServing}p`);
  if (recipe.carbsPerServing > 0) parts.push(`${recipe.carbsPerServing}c`);
  if (recipe.fatPerServing > 0) parts.push(`${recipe.fatPerServing}f`);
  return parts.join(" · ");
}
